import { useState, useEffect } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';

import { addCertification, updateCertification, getCertifications } from '../../api.js';
import { useAuth } from "../../AuthContext"
import { Badge } from '../Badge/Badge';

export const CertificationForm = ({ open, onClose, certification, onSave }) => {
    const [ allCerts, setAllCerts ] = useState([]);
    const [ selectedPrereqs, setSelectedPrereqs ] = useState([]);
    const [ formData, setFormData ] = useState({
        name: '',
        badge: '',
        color: '#4CAF50',
        badgeText: '',
        refresh: 90,
        type: 'primary',
        prerequisites: {},
    });

    const { token } = useAuth();

    const generateRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    const handleRefreshColor = () => {
        setFormData(prev => ({ ...prev, color: generateRandomColor() }));
    };

    useEffect(() => {
        if (certification) {
            setFormData({
                name: certification.name || '',
                badge: certification.badge || '',
                color: certification.color || '#4CAF50',
                badgeText: certification.badgeText || '',
                refresh: certification.refresh || 90,
                type: certification.type || 'primary',
                prerequisites: certification.prerequisites || {},
            });

            setSelectedPrereqs(Object.keys(certification.prerequisites || {}));
        } else {
            setFormData({
                name: '',
                badge: '',
                color: generateRandomColor(),
                badgeText: '',
                refresh: 90,
                type: 'primary',
                prerequisites: {},
            });

            setSelectedPrereqs([]);
        }
    }, [ certification ]);

    useEffect(() => {
        const fetchCerts = async () => {
            const certs = await getCertifications(token);

            if (!certs.error) {
                setAllCerts(certs);
            }
        };

        fetchCerts();
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => ({ ...prev, [ name ]: value }));
    };

    const handlePrereqsChange = (event, value) => {
        setSelectedPrereqs(value.map(c => c.id));

        const prereqs = {};

        value.forEach(c => prereqs[ c.id ] = true);

        setFormData(prev => ({ ...prev, prerequisites: prereqs }));
    };

    const handleSubmit = async () => {
        const data = { ...formData };

        if (certification) {
            data.id = certification.id;

            const result = await updateCertification(data, token);

            if (!result.error) {
                onSave();
                onClose();
            }
        } else {
            const result = await addCertification(data, token);

            if (!result.error) {
                onSave();
                onClose();
            }
        }
    };

    return (
        <Dialog open={ open } onClose={ onClose } maxWidth="sm" fullWidth>
            <DialogTitle>{ certification ? 'Edit Certification' : 'Add Certification' }</DialogTitle>

            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    name="name"
                    label="Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={ formData.name }
                    onChange={ handleChange }
                />

                <Box display="flex" alignItems="center" gap={1} marginY={1}>
                    <TextField
                        name="color"
                        label="Badge Color (hex)"
                        type="text"
                        variant="outlined"
                        value={ formData.color }
                        onChange={ handleChange }
                        placeholder="#4CAF50"
                        sx={{ flexGrow: 1 }}
                    />
                    <Tooltip
                        title={
                            <Box sx={{ width: 100, height: 100 }}>
                                <Badge
                                    certification={{
                                        id: 'preview-large',
                                        name: formData.name || 'Preview',
                                        badge: formData.badge,
                                        color: formData.color,
                                        badgeText: formData.badgeText,
                                    }}
                                    certified={true}
                                    loading={false}
                                />
                            </Box>
                        }
                    >
                        <Box sx={{ width: 50, height: 50, cursor: 'pointer' }}>
                            <Badge
                                certification={{
                                    id: 'preview',
                                    name: formData.name || 'Preview',
                                    badge: formData.badge,
                                    color: formData.color,
                                    badgeText: formData.badgeText,
                                }}
                                certified={true}
                                loading={false}
                            />
                        </Box>
                    </Tooltip>
                    <IconButton onClick={handleRefreshColor}>
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <TextField
                    margin="dense"
                    name="badge"
                    label="Badge Text"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={ formData.badge }
                    onChange={ handleChange }
                    placeholder="Badge text"
                />

                <TextField
                    margin="dense"
                    name="refresh"
                    label="Refresh (days)"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={ formData.refresh }
                    onChange={ handleChange }
                />

                <FormControl fullWidth margin="dense">
                    <InputLabel>Type</InputLabel>

                    <Select
                        name="type"
                        value={ formData.type }
                        onChange={ handleChange }
                        label="Type"
                    >
                        <MenuItem value="primary">Primary</MenuItem>
                        <MenuItem value="endorsement">Endorsement</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                    </Select>
                </FormControl>

                <Autocomplete
                    multiple
                    options={ allCerts }
                    getOptionLabel={ (option) => option.name }
                    value={ allCerts.filter(c => selectedPrereqs.includes(c.id)) }
                    onChange={ handlePrereqsChange }
                    renderValue={ (value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip key={ option.id } variant="outlined" label={ option.name } { ...getTagProps({ index }) } />
                        ))
                    }

                    renderInput={ (params) => (
                        <TextField
                            { ...params }
                            variant="outlined"
                            label="Prerequisites"
                            placeholder="Select prerequisites"
                        />
                    ) }
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={ onClose }>Cancel</Button>

                <Button onClick={ handleSubmit } variant="contained">
                    { certification ? 'Update' : 'Add' }
                </Button>
            </DialogActions>
        </Dialog>
    );
};